/**
 * **A submitted survey, read on its task card** — every answer beside the previous one, and under
 * them the one that moved most, charted over the check-ins (owner, 24 September 2026). The history is
 * the one the Ankiety tab and `/raporty`'s report already read (`GET /customer/{id}/surveys`), so the
 * card costs no read of its own that the client detail does not make anyway.
 *
 * It lives HERE rather than in `features/attention-queue` because a feature may not import a
 * feature: the task card takes it as a slot, the way it takes the session note's composer.
 *
 * Densities: `desk` (`/zadania`) draws the WHOLE occurrence — every reading, the measurement, the
 * media, each answer in words quoted — and the chart across the full width under it, as tall as the
 * body leaves room for (owner, 25 September 2026), so the card is read whole without scrolling. The two small cards lost their big figure the
 * same day and draw the answers themselves instead — the three that moved most, the previous answer
 * beside the new one (`readingsByChange`): `quick` (the Przegląd's card) with the chart of the one
 * that moved most beside them, `short` (the Pulpit's) the three rows alone.
 *
 * ── The box stands before the history lands ──────────────────────────────────────────────────
 * The reserve is the loaded shape's; the gate draws its bars. A history that answered with nothing
 * to compare (a first check-in) draws the answers and no chart — never an empty plot.
 *
 * ── The history is read once the card has been ON SCREEN ─────────────────────────────────────
 * The Pulpit draws every survey task it holds in a scrolling card, so a read per card on the first
 * paint was a `GET /customer/{id}/surveys` per row — whole histories for rows nobody has scrolled to.
 * The read waits for the box to be seen (`shared/lib/useOnScreen`), the same gate the session and
 * clip reads stand behind; the reserve stands meanwhile, so a row scrolls in already its size.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { numberFormat } from '~/shared/lib/intlCache';
import { useOnScreen } from '~/shared/lib/useOnScreen';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { ReadoutChart } from '~/shared/ui/chart/ReadoutChart';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useReadingLabels } from '../lib/useReadingLabels';
import { useSurveyHistorySource } from '../lib/useSurveyHistorySource';
import { biggestChange } from '../model/biggestChange';
import { buildSurveyHistory, SCALE_STEPS } from '../model/surveyHistory';
import {
  buildSurveyReport,
  readingsByChange,
  type SurveyReportEntry,
  type SurveyReportView,
} from '../model/surveyReport';
import { HUE_INK } from './seriesInk';
import { ChangeArrowIcon } from './SurveyIcons';
import { SurveyReading, TONE_CLASS } from './SurveyReading';
import styles from './SurveyTaskReading.module.css';

/** The whole history, because the card compares to the previous answer whatever its date. */
const WHOLE_RANGE = { from: '0001-01-01', to: '9999-12-31' } as const;

/** The desk's rows past which they stand side by side (owner, 25 September 2026). */
const DESK_ONE_COLUMN_ROWS = 4;

/** The rows a small card has room for (owner, 25 September 2026). */
const SMALL_CARD_ROWS = 3;

interface SurveyTaskReadingProps {
  readonly customerId: number;
  readonly surveyAssignmentId: number;
  readonly density: 'desk' | 'quick' | 'short';
  readonly reducedMotion: boolean;
}

export function SurveyTaskReading({ customerId, surveyAssignmentId, density, reducedMotion }: SurveyTaskReadingProps) {
  const { t, i18n } = useTranslation();
  const labels = useReadingLabels();
  const boxRef = useRef<HTMLDivElement>(null);
  const { source, exerciseNames, pending, failed } = useSurveyHistorySource(customerId, useOnScreen(boxRef));
  const gate = useSkeletonGate(pending);
  const view = buildSurveyHistory(source, {
    t,
    locale: i18n.language,
    chartRange: WHOLE_RANGE,
    occurrenceId: surveyAssignmentId,
    exerciseNames,
  });
  const report = gate.waiting ? null : buildSurveyReport(view, surveyAssignmentId);
  const change = gate.waiting ? null : biggestChange(view, surveyAssignmentId);
  // The desk's plot takes the height its box is given (`.deskPlot`): what is left under the answers.
  const plotHeight = density === 'desk' ? '100%' : 'var(--h-task-survey-plot-quick)';

  if (gate.waiting) {
    return (
      <div ref={boxRef} className={`${String(styles.reading)} ${styles[density] ?? ''}`} aria-busy="true">
        <span className={`${String(styles.reserve)} ${gate.skeleton ? String(skel.bar) : ''}`} />
        <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
      </div>
    );
  }

  if (failed) {
    return (
      <div ref={boxRef} className={`${String(styles.reading)} ${styles[density] ?? ''} ${gate.reveal}`}>
        <p className={styles.failed}>{t('web:tasks.blocks.readFailed')}</p>
      </div>
    );
  }

  return (
    <div ref={boxRef} className={`${String(styles.reading)} ${styles[density] ?? ''} ${gate.reveal}`}>
      {density === 'desk' && report !== null ? deskAnswers(report) : null}

      {density !== 'desk' && report !== null && report.readings.length > 0 ? (
        <dl className={styles.compact}>
          {readingsByChange(report.readings, SMALL_CARD_ROWS).map((entry) => (
            <div className={styles.compactRow} key={entry.key}>
              <dt className={styles.question}>{entry.label}</dt>
              <dd className={styles.compactValue}>{compactValue(entry)}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {change === null || density === 'short' ? null : (
        <div className={styles.moved}>
          <p className={styles.movedHead}>
            <b>{t('web:tasks.blocks.survey.moved', { question: change.row.shortLabel })}</b>
            <span>
              {t('web:tasks.blocks.survey.movedFigures', {
                from: formatNumber(change.previous),
                to: formatNumber(change.current),
                of: change.row.kind === 'scale' ? `/${String(SCALE_STEPS)}` : (change.row.unit ?? ''),
              })}{' '}
              <em className={`${String(styles.change)} ${TONE_CLASS[change.tone]}`}>
                <ChangeArrowIcon sign={Math.sign(change.delta)} />
                {/* The arrow is a glyph; a screen reader hears the signed figure instead. */}
                <span aria-hidden="true">{formatNumber(Math.abs(change.delta))}</span>
                <span className={hidden.srOnly}>
                  {numberFormat(i18n.language, { maximumFractionDigits: 1, signDisplay: 'exceptZero' }).format(change.delta)}
                </span>
              </em>
            </span>
          </p>
          <ReadoutChart
            points={change.series.points}
            colour={HUE_INK[change.series.hue]}
            height={plotHeight}
            name={change.series.name}
            unit={change.series.unit ?? undefined}
            zeroBaseline={false}
            compact
            extraYValues={change.row.kind === 'scale' ? [1, SCALE_STEPS] : undefined}
            reducedMotion={reducedMotion}
            className={density === 'desk' ? styles.deskPlot : undefined}
          />
        </div>
      )}
    </div>
  );

  /**
   * The whole occurrence on the desk (owner, 25 September 2026): every reading with its change, the
   * full measurement's completeness, the media the client sent, and each answer in words as a short
   * quote — one compact row apiece, side by side once there are more than a column's worth.
   */
  function deskAnswers(whole: SurveyReportView) {
    const rows =
      whole.readings.length + (whole.measurement === null ? 0 : 1) + whole.media.length + whole.answers.length;
    return (
      <dl className={`${String(styles.answers)} ${rows > DESK_ONE_COLUMN_ROWS ? String(styles.answersDense) : ''}`}>
        {whole.readings.map((entry) => (
          <div className={styles.answer} key={entry.key}>
            <dt className={styles.question}>{entry.label}</dt>
            <dd className={styles.value}>
              <SurveyReading cell={entry.cell} kind={entry.kind} showDelta showScaleFigure labels={labels} />
            </dd>
          </div>
        ))}
        {whole.measurement === null ? null : (
          <div className={styles.answer}>
            <dt className={styles.question}>{t('web:clientDetail.surveys.bandMeasurement')}</dt>
            <dd className={`${String(styles.value)} ${String(styles.valueWords)}`}>
              {whole.measurement.caption ?? labels.blankLabel}
            </dd>
          </div>
        )}
        {whole.media.map((entry) => (
          <div className={styles.answer} key={entry.key}>
            <dt className={styles.question}>{entry.label}</dt>
            <dd className={`${String(styles.value)} ${String(styles.valueWords)}`}>{mediaWords(entry)}</dd>
          </div>
        ))}
        {whole.answers.map((entry) => (
          <div className={`${String(styles.answer)} ${String(styles.answerWords)}`} key={entry.key}>
            <dt className={styles.question}>{entry.label}</dt>
            <dd className={styles.value}>
              {entry.cell.state === 'value' && entry.cell.text !== null ? (
                <q className={styles.quote}>{entry.cell.text}</q>
              ) : (
                <SurveyReading cell={entry.cell} kind={entry.kind} showDelta={false} labels={labels} />
              )}
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  /** What arrived, in words: how many poses, or how long the clip is and when it ages out. */
  function mediaWords(entry: SurveyReportEntry): string {
    if (entry.cell.state !== 'value') return labels.blankLabel;
    if (entry.kind === 'photos') return t('web:clientDetail.surveys.poseCount', { count: entry.cell.photos.length });
    const clip = entry.cell.clip;
    if (clip === null) return labels.blankLabel;
    if (clip.expired) return labels.expiredLabel;
    return [clip.durationLabel, clip.expiryLabel].filter((part) => part !== null && part !== '').join(' · ');
  }

  /**
   * The previous answer, an arrow, the new one — „68,4 → 67,9 kg" — where there is a number to
   * compare; the answer as the report draws it everywhere else.
   */
  function compactValue(entry: SurveyReportEntry) {
    const cell = entry.cell;
    if (cell.number === null || cell.delta === null) {
      return <SurveyReading cell={cell} kind={entry.kind} showDelta={false} showScaleFigure labels={labels} />;
    }
    return (
      <>
        <span className={styles.compactBefore}>{formatNumber(cell.number - cell.delta)}</span>
        <em className={`${String(styles.change)} ${TONE_CLASS[cell.deltaTone ?? 'flat']}`}>
          <ChangeArrowIcon sign={Math.sign(cell.delta)} />
        </em>
        <span>
          {formatNumber(cell.number)}
          {entry.kind === 'scale' ? `/${String(SCALE_STEPS)}` : cell.unit === null ? '' : ` ${cell.unit}`}
        </span>
      </>
    );
  }

  function formatNumber(value: number): string {
    return numberFormat(i18n.language, { maximumFractionDigits: 1 }).format(value);
  }
}
